import { useEffect, useState } from 'react';
import { Button, Divider, LinearProgress, MobileStepper, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useNavigate, useLocation } from 'react-router-dom';
import { getQueryParamsFromLocation, setUrl } from 'actions/url';
import { advisoriesLoadingSelector } from 'slice-reducers/locationAndAdvisoriesSlice';
import { geolocationErrorSelector, userLocationLoadingSelector } from 'slice-reducers/appSlice';
import { createNotifyPolygonFeatureCollection } from 'actions/mapBoxAndTurf';
import { useDispatch, useSelector } from 'react-redux';
import GeolocationErrorMessage from 'components/rightPanel/searchAndAdvisories/GeolocationErrorMessage';
import UserLocationLoading from 'components/rightPanel/searchAndAdvisories/UserLocationLoading';
import NotifyDisclaimer from 'components/rightPanel/notify/NotifyDisclaimer';
import NotifyForm from 'components/rightPanel/notify/NotifyForm';
import { ReactComponent as NotifyAndFlyIllustration } from 'assets/icons/notifyAndFlyIllustration.svg';
import {
	mapLayersSelector,
	mapPinCoordinatesSelector,
	notifyAreaSelector,
	radiusSelector,
	setNotifyArea,
	setRadius,
	setRefreshNotifyAndFlyLayer,
	setVisibleMapLayers,
	visibleMapLayersSelector
} from 'slice-reducers/mapSlice';
import { defaultFormFields, notifyAreaFeatureId } from 'constants/notifyAndFlyConstants';
import { submitNotify } from 'apis/notify.api';
import { notifyAndFlyLayerId } from 'constants/mapConstants';
import { convertFeetToMiles, getSelectedMapLayerList, toggleMapLayer } from 'utils/mapUtils';
import { browserTimeZone } from 'constants/timeConstants';
import dayjs from 'utils/customDayJS';
import circle from '@turf/circle';
import { featureCollection } from '@turf/helpers';
import { formatDateAsTimeBrowserTimezone } from 'utils/timeUtils';
import { getSelectedMapLayersFromLS, saveSelectedMapLayersToLS } from 'actions/localStorage';

const styles = {
	main: {
		width: '100%',
		height: '100%'
	},
	header: {
		textAlign: 'center',
		padding: 20
	},
	cancelButton: {
		position: 'fixed',
		right: 20,
		textTransform: 'none'
	},
	title: {
		fontWeight: 'bold'
	},
	belowHeader: {
		width: '100%',
		height: 'calc(100% - 193px)'
	},
	stepContainer: {
		width: '100%',
		height: '100%'
	},
	stepperMain: {
		width: '100%',
		backgroundColor: 'inherit'
	},
	top: {
		width: '100%',
		height: 'calc(100% - 52px)'
	},
	buttonPlaceholder: {
		height: 36,
		width: 76
	},
	lineOne: {
		fontWeight: 700,
		fontSize: '2rem'
	},
	lineTwo: {
		lineHeight: 1,
		padding: '1rem 1.5rem 0px'
	}
};

const NotifyContainer = () => {
	const useStyles = makeStyles(styles);
	const classes = useStyles();
	const location = useLocation();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const queryParams = getQueryParamsFromLocation(location);
	const userLocationLoading = useSelector(userLocationLoadingSelector);
	const geolocationError = useSelector(geolocationErrorSelector);
	const mapPinCoordinates = useSelector(mapPinCoordinatesSelector);
	const advisoriesLoading = useSelector(advisoriesLoadingSelector);
	const notifyArea = useSelector(notifyAreaSelector);
	const mapLayers = useSelector(mapLayersSelector);
	const visibleMapLayers = useSelector(visibleMapLayersSelector);
	const localStorageSelectedMapLayers = getSelectedMapLayersFromLS();
	const radiusObject = useSelector(radiusSelector);
	const showLocationLoading = (userLocationLoading && !geolocationError) || advisoriesLoading;
	const showGeolocationError =
		geolocationError && (!mapPinCoordinates.lat || !mapPinCoordinates.long);
	const [submitting, setSubmitting] = useState(false);
	const [activeStep, setActiveStep] = useState(1);
	const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
	const [formFields, setFormFields] = useState(defaultFormFields);
	const { ceiling, duration, start_time } = formFields;
	const [recaptchaValue, setRecaptchaValue] = useState(null);
	const [formErrors, setFormErrors] = useState(false);
	const submitDisabled = submitting || formErrors || !recaptchaValue;

	const handleCancel = () => {
		setUrl({ queryObject: queryParams, navigate, path: '/' });
	};

	const handleBack = () => {
		setActiveStep(1);
		setFormFields(defaultFormFields);
		setRecaptchaValue(null);
	};

	const handleStartTime = value => {
		setFormFields({
			...formFields,
			start_time: value
		});
	};

	const handleChangeRadius = e => {
		let updatedRadiusInFeet;
		if (e.target.value > 8000) {
			// max value on radius
			updatedRadiusInFeet = 8000;
			setFormErrors(true);
		} else if (e.target.value < 600) {
			// min value on radius
			updatedRadiusInFeet = 600;
			setFormErrors(true);
		} else {
			updatedRadiusInFeet = Number(e.target.value);
			setFormErrors(false);
		}
		const currentCenter = notifyArea?.features[0].properties?.center;
		const radiusInMiles = convertFeetToMiles(updatedRadiusInFeet);
		const updatedWithNewRadius = circle(currentCenter, radiusInMiles, {
			units: 'miles',
			properties: { isCircle: true, center: currentCenter, radiusInFeet: updatedRadiusInFeet }
		});
		dispatch(setNotifyArea(featureCollection([updatedWithNewRadius])));
		dispatch(setRadius(Number(e.target.value)));
	};

	const handleSubmit = async () => {
		const { type, ceiling, start_time, duration } = formFields;
		setSubmitting(true);
		const payload = {
			volume: notifyArea.features[0],
			type,
			ceiling,
			duration,
			date: dayjs(start_time).unix(),
			timezone: browserTimeZone,
			captcha: recaptchaValue
		};
		const res = await dispatch(submitNotify(payload));
		if (res) {
			dispatch(setRefreshNotifyAndFlyLayer(true));
			setSubmitting(false);
			handleCancel();
		}
	};

	const handleNext = () => {
		setActiveStep(2);
		setFormFields({
			...formFields,
			// set start_time to one minute ahead of current time each time form goes to page 2
			start_time: formatDateAsTimeBrowserTimezone(new Date()).add(1, 'minute')
		});
	};

	useEffect(() => {
		// if notify area is null, set it to default circle shape using queryParams as center
		if (notifyArea === null && queryParams.lat && queryParams.long) {
			const notifyAreaCol = createNotifyPolygonFeatureCollection({
				lat: Number(queryParams.lat),
				long: Number(queryParams.long)
			});
			const notifyAreaColWithId = {
				...notifyAreaCol,
				features: [
					{
						...notifyAreaCol.features[0],
						id: notifyAreaFeatureId
					}
				]
			};
			dispatch(setRadius(notifyAreaCol.features[0].properties.radiusInFeet));
			dispatch(setNotifyArea(notifyAreaColWithId));
		}
	}, [dispatch, notifyArea, queryParams.lat, queryParams.long]);

	useEffect(() => {
		// Remove notify area from map and remove radius on unmount
		return () => {
			dispatch(setRadius(null));
			dispatch(setNotifyArea(null));
		};
	}, [dispatch]);

	useEffect(() => {
		//Ensure notify-and-fly layer is toggled on
		if (mapLayers.length > 0 && !localStorageSelectedMapLayers[notifyAndFlyLayerId]) {
			const updatedSettings = toggleMapLayer({
				currentSettings: localStorageSelectedMapLayers,
				layerId: notifyAndFlyLayerId
			});
			saveSelectedMapLayersToLS(updatedSettings);
			const visibleMapLayers = getSelectedMapLayerList({
				allLayers: mapLayers,
				currentSettings: updatedSettings
			});
			dispatch(setVisibleMapLayers(visibleMapLayers));
		}
	}, [visibleMapLayers, dispatch, mapLayers, localStorageSelectedMapLayers]);

	useEffect(() => {
		if (
			ceiling < 25 ||
			ceiling > 400 ||
			duration < 1 ||
			duration > 60 ||
			dayjs(start_time).isBefore()
		) {
			setFormErrors(true);
		} else {
			setFormErrors(false);
		}
	}, [ceiling, duration, start_time]);

	return (
		<div className={classes.main}>
			<div className={classes.header}>
				<Button disabled={submitting} onClick={handleCancel} className={classes.cancelButton}>
					Cancel
				</Button>
				<div>
					<NotifyAndFlyIllustration />
					<Typography component='h5' variant='h5' className={classes.title}>
						Notify & Fly
					</Typography>
					<Typography color='textSecondary'>Click map to move flight area</Typography>
				</div>
			</div>
			<Divider />
			<div className={classes.belowHeader}>
				{showLocationLoading && <UserLocationLoading />}
				{showGeolocationError && <GeolocationErrorMessage />}
				{!showLocationLoading && (
					<div className={classes.stepContainer}>
						<div className={classes.top}>
							{submitting && <LinearProgress />}
							{activeStep === 1 && <NotifyDisclaimer />}
							{activeStep === 2 && (
								<NotifyForm
									formFields={formFields}
									setFormFields={setFormFields}
									recaptchaLoaded={recaptchaLoaded}
									setRecaptchaLoaded={setRecaptchaLoaded}
									setRecaptchaValue={setRecaptchaValue}
									setTime={handleStartTime}
									radiusObject={radiusObject}
									handleChangeRadius={handleChangeRadius}
								/>
							)}
						</div>
						<MobileStepper
							className={classes.stepperMain}
							variant='dots'
							steps={2}
							position='static'
							activeStep={activeStep - 1}
							nextButton={
								activeStep === 1 ? (
									<Button color='primary' onClick={handleNext}>
										Next
									</Button>
								) : (
									<Button color='primary' disabled={submitDisabled} onClick={handleSubmit}>
										File Flight
									</Button>
								)
							}
							backButton={
								activeStep === 1 ? (
									<div className={classes.buttonPlaceholder} />
								) : (
									<Button onClick={handleBack} disabled={activeStep === 1 || submitting}>
										Back
									</Button>
								)
							}
						/>
					</div>
				)}
			</div>
		</div>
	);
};

export default NotifyContainer;
